import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from 'axios';
import type { ApiConfig, ApiResponse, RetryConfig } from '@core/types/api.types.ts';
import { AppApiError } from '@core/errors/api-errors.ts';
import { AuthManager } from './auth-manager.ts';

// ═══════════════════════════════════════════
// DEFAULTS
// ═══════════════════════════════════════════

const DEFAULT_RETRY: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  retryOn: [408, 429, 500, 502, 503, 504],
  exponentialBackoff: true,
};

const DEFAULT_TIMEOUT = 30_000;

// ═══════════════════════════════════════════
// Extended config type for internal tracking
// ═══════════════════════════════════════════

interface InternalRequestConfig extends InternalAxiosRequestConfig {
  _retry?: number;
  _skipAuth?: boolean;
  _skipRetry?: boolean;
  _startTime?: number;
}

// ═══════════════════════════════════════════
// HTTP CLIENT CLASS
// ═══════════════════════════════════════════

export class HttpClient {
  private instance: AxiosInstance;
  private authManager: AuthManager | null = null;
  private retryConfig: RetryConfig;
  private enableLogging: boolean;

  constructor(config: ApiConfig) {
    this.retryConfig = { ...DEFAULT_RETRY, ...config.retry };
    this.enableLogging = config.logging ?? false;

    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout ?? DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...config.headers,
      },
    });

    if (config.auth) {
      this.authManager = new AuthManager(config.auth);
    }

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  // ─────────────────────────────────────────
  // INTERCEPTOR: REQUEST
  // ─────────────────────────────────────────

  private setupRequestInterceptor(): void {
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const internal = config as InternalRequestConfig;

        if (this.authManager && !internal._skipAuth) {
          const token = this.authManager.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        config.headers['X-Request-ID'] = crypto.randomUUID();
        internal._startTime = Date.now();

        if (this.enableLogging) {
          console.log(`[API] → ${config.method?.toUpperCase()} ${config.url}`);
          if (config.data !== undefined) {
            console.log(`[API] ↑ body ${config.url}`, config.data);
          }
        }

        return config;
      },
      (error) => Promise.reject(AppApiError.fromAxiosError(error as AxiosError)),
    );
  }

  // ─────────────────────────────────────────
  // INTERCEPTOR: RESPONSE
  // ─────────────────────────────────────────

  private setupResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.enableLogging) {
          const internal = response.config as InternalRequestConfig;
          const duration = Date.now() - (internal._startTime || 0);
          console.log(`[API] ← ${response.status} ${response.config.url} (${duration}ms)`);
        }
        return response;
      },

      async (error: unknown) => {
        const axiosError = error as AxiosError;
        const originalRequest = axiosError.config as InternalRequestConfig | undefined;
        if (!originalRequest) return Promise.reject(AppApiError.fromAxiosError(axiosError));

        if (this.enableLogging && axiosError.response) {
          console.error(
            `[API] ✗ ${axiosError.response.status} ${originalRequest.url}`,
            { requestBody: originalRequest.data, responseBody: axiosError.response.data },
          );
        }

        // CASO 1: Token expirado → Refresh
        if (
          axiosError.response?.status === 401 &&
          this.authManager &&
          !originalRequest._skipAuth &&
          (originalRequest._retry ?? 0) === 0
        ) {
          originalRequest._retry = 1;
          try {
            const newToken = await this.authManager.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.instance(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // CASO 2: Error retryable → Retry con backoff
        if (
          !originalRequest._skipRetry &&
          axiosError.response &&
          this.retryConfig.retryOn.includes(axiosError.response.status) &&
          (originalRequest._retry ?? 0) < this.retryConfig.maxRetries
        ) {
          originalRequest._retry = (originalRequest._retry ?? 0) + 1;

          const delay = this.retryConfig.exponentialBackoff
            ? this.retryConfig.retryDelay * Math.pow(2, originalRequest._retry - 1)
            : this.retryConfig.retryDelay;

          const jitter = delay * 0.1 * Math.random();

          if (this.enableLogging) {
            console.warn(
              `[API] ↻ Retry ${originalRequest._retry}/${this.retryConfig.maxRetries} ` +
                `para ${originalRequest.url} en ${Math.round(delay + jitter)}ms`,
            );
          }

          await new Promise((resolve) => setTimeout(resolve, delay + jitter));
          return this.instance(originalRequest);
        }

        // CASO 3: Error final
        return Promise.reject(AppApiError.fromAxiosError(axiosError));
      },
    );
  }

  // ─────────────────────────────────────────
  // MÉTODOS PÚBLICOS
  // ─────────────────────────────────────────

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.get<T>(url, config);
    return this.normalizeResponse(response);
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.post<T>(url, data, config);
    return this.normalizeResponse(response);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.put<T>(url, data, config);
    return this.normalizeResponse(response);
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<T>(url, data, config);
    return this.normalizeResponse(response);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<T>(url, config);
    return this.normalizeResponse(response);
  }

  /**
   * Request con cancelación automática.
   * Devuelve [promise, abortFn].
   */
  withCancel<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    url: string,
    dataOrConfig?: unknown,
  ): [Promise<ApiResponse<T>>, () => void] {
    const controller = new AbortController();
    const config: AxiosRequestConfig = { signal: controller.signal };
    let promise: Promise<ApiResponse<T>>;

    if (method === 'get' || method === 'delete') {
      promise = this[method]<T>(url, config);
    } else {
      promise = this[method]<T>(url, dataOrConfig, config);
    }

    return [promise, () => controller.abort()];
  }

  get auth(): AuthManager | null {
    return this.authManager;
  }

  // ─────────────────────────────────────────
  // HELPERS PRIVADOS
  // ─────────────────────────────────────────

  private normalizeResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
      meta: {
        timestamp: new Date().toISOString(),
        requestId: response.config.headers?.['X-Request-ID'] as string | undefined,
        rateLimit: this.extractRateLimitHeaders(response),
      },
    };
  }

  private extractRateLimitHeaders(
    response: AxiosResponse,
  ): { limit: number; remaining: number; resetAt: string } | undefined {
    const limit = response.headers['x-ratelimit-limit'];
    if (!limit) return undefined;
    return {
      limit: Number(limit),
      remaining: Number(response.headers['x-ratelimit-remaining']),
      resetAt: response.headers['x-ratelimit-reset'] as string,
    };
  }
}
